import {
    snapshotConfig,
    SnapshotInterface,
    SnapshotType,
} from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import { useSnapshotQueryData } from 'app/agriculture/queries/snapshots/useSnapshotQueryData';
import { Duration, formatDuration } from 'date-fns';
import { filterSnapshots } from 'app/agriculture/queries/snapshots/utils/filterSnapshots';
import { Snapshot } from 'app/agriculture/queries/snapshots/types/Snapshot';
import { useMemo } from 'react';
import { TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';
import { useCursorTimeForSnapshotType } from 'app/agriculture/queries/snapshots/useCursorTimeForSnapshotType';

/**
 * Returns the latest snapshot before the cursor time, but only if it's timestamp is less than a shelf life ago
 * @param parentId returns null if parentId is undefined
 * @param snapshotType
 * @param shelfLifeOverride defaults to snapshotConfig.ts
 * @param cursorTimeOverride defaults to timeline cursor time
 */
export function useSnapshot<T extends SnapshotType, I extends Snapshot = SnapshotInterface<T>>({
    parentId,
    snapshotType,
    shelfLife: shelfLifeOverride,
    cursorTimeOverride,
}: {
    parentId?: string;
    snapshotType: T;
    shelfLife?: Duration | null;
    cursorTimeOverride?: Date;
}): I | null {
    const { timeZone } = useTimeZone();
    const { shelfLife: configShelfLife } = snapshotConfig[snapshotType];
    const shelfLife = shelfLifeOverride === undefined ? configShelfLife : shelfLifeOverride;

    const cursorTime = useCursorTimeForSnapshotType({ snapshotType, cursorTimeOverride });

    const snapshotQueryData = useSnapshotQueryData<T, I>({
        snapshotType,
        shelfLife,
        cursorTime,
    });

    return useMemo(() => {
        if (parentId == null) return null;

        const snapshotsForParent = snapshotQueryData.get(parentId);

        if (!snapshotsForParent || snapshotsForParent.length === 0) return null;

        const filteredSnapshots = filterSnapshots<I>(snapshotsForParent, {
            shelfLife,
            cursorTime,
        });

        const [latestSnapshot] = filteredSnapshots;

        if (latestSnapshot == null) return null;

        return { ...latestSnapshot, timestamp: new TZDate(latestSnapshot.timestamp, timeZone) };
    }, [
        snapshotQueryData,
        // this is to catch new shelf life duration objects with the same value
        shelfLife == null ? shelfLife : formatDuration(shelfLife),
        cursorTime,
        timeZone,
        parentId,
    ]);
}
